import { IOutFeaturedDealDTO } from "@app/components/deal-cards/new-deal-card/deal-card.interface";
import { DEAL_OFFER_PROPERTIES, DEAL_PRODUCT_PROPERTIES } from "./deal-node-properties";
import { JsonLdNode, toJsonLdGraph } from "./to-json-ld";

export interface IBuildPageStructuredDataProps {
  /** Nodes describing the page itself and its main entity. They come first in the graph. */
  pageNodes?: JsonLdNode[];
  /** Every deal shown on the page, from any section. Repeats are expected and merged. */
  deals?: IOutFeaturedDealDTO[];
  /** Deals already described as Offers of the main entity; they get no Product node of their own. */
  excludeDealIds?: number[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Fills the properties `target` lacks with those of `source`, but only the listed ones. Existing
 * values are never replaced, so the first copy of a deal keeps precedence and a later copy can only
 * complete it. A property outside the list is ignored even if `source` carries it.
 */
const fillMissing = (
  target: Record<string, unknown>,
  source: Record<string, unknown>,
  properties: readonly string[]
): void => {
  properties.forEach((property) => {
    if (target[property] === undefined && source[property] !== undefined) {
      target[property] = source[property];
    }
  });
};

/**
 * Merges a second copy of a deal's Product node into the first. Different endpoints serialise a
 * deal differently (`/search` carried no `categories` for a long time), so the copy that came first
 * is not always the most complete one. Only known Product and Offer properties cross over, and only
 * when missing, so the merged node can never say something neither copy said.
 *
 * `offers` is handled apart from the property list: an Offer the first copy lacks is adopted as a
 * copy, never by reference, so later merges keep writing into objects this function owns.
 */
const mergeDealNode = (target: JsonLdNode, source: JsonLdNode): void => {
  if (target["@type"] !== source["@type"]) return;

  fillMissing(target, source, DEAL_PRODUCT_PROPERTIES);

  if (!isPlainObject(source.offers)) return;

  if (!isPlainObject(target.offers)) {
    target.offers = { ...source.offers };

    return;
  }

  if (target.offers["@type"] === source.offers["@type"]) {
    fillMissing(target.offers, source.offers, DEAL_OFFER_PROPERTIES);
  }
};

/**
 * A copy of the node with its own `offers` object. The merge writes into the node and into
 * `offers`, never deeper, so this is exactly what it needs to own. The original stays as it is on
 * the deal, which also lives in the redux state.
 */
const copyDealNode = (node: JsonLdNode): JsonLdNode => ({
  ...node,
  ...(isPlainObject(node.offers) ? { offers: { ...node.offers } } : {})
});

/**
 * The one JSON-LD block a page emits.
 *
 * Deals reach the page through several sections (featured blocks, the overview, sponsored slots,
 * search results), and the CMS decides per page which sections exist, so no section can know what
 * another one shows. Composing the graph here, after every section is mapped, is the only point
 * where a deal can be guaranteed to appear once. Nodes are keyed by `@id`, which is what Google
 * merges on; a repeated `@id` doubles every property of the merged entity. A repeat is merged into
 * the first copy so no property is lost when the copies differ. On a conflict the first copy wins:
 * the order is the CMS order of the sections, and preferring one endpoint over another would only
 * move the arbitrariness, not remove it.
 *
 * Returns undefined when there is nothing to describe, so the caller can filter it out.
 */
export const buildPageStructuredData = (props: IBuildPageStructuredDataProps): string | undefined => {
  const { pageNodes = [], deals = [], excludeDealIds = [] } = props;

  const excluded = new Set(excludeDealIds);
  const pageNodeIds = new Set(pageNodes.map((node) => node["@id"]).filter(Boolean));
  const dealNodesById = new Map<string, JsonLdNode>();
  const dealNodes: JsonLdNode[] = [];

  deals.forEach((deal) => {
    const node = deal.structuredData;

    if (!node || excluded.has(deal.id)) return;

    const id = node["@id"];

    // A deal that is also a page node (never the case today) is already described.
    if (id && pageNodeIds.has(id)) return;

    const existing = id ? dealNodesById.get(id) : undefined;

    if (existing) {
      mergeDealNode(existing, node);

      return;
    }

    const copy = copyDealNode(node);

    if (id) dealNodesById.set(id, copy);
    dealNodes.push(copy);
  });

  const nodes = [...pageNodes, ...dealNodes];

  return nodes.length ? toJsonLdGraph(nodes) : undefined;
};
